import './styles.scss';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

import {
  Tooltip, OverlayTrigger, Container, Icon, IconButton,
} from '@edx/paragon';
import {
  Check, InfoOutline, Remove, Launch,
} from '@edx/paragon/icons';
import { injectIntl, defineMessages, FormattedMessage } from 'gatsby-plugin-react-intl';

import { intlShape } from '@prospectus/common/types/intlShape';
import ProspectusLink from '@prospectus/common/ui-strict/ProspectusLink';
import { formatPrice, getI18nDayMonthString, getI18nDateString } from '@utils/formatting';
import { defaultUserCurrency } from '@utils/i18n';
import { getCourseStartDate, getCourseRunTilePrimaryText } from '@utils/courseRunSelection';
import { ShowAuditModeExpirationDateContext } from '@utils/context';
import { endsWithinThisYear } from '@utils/date';
import { currencyInfoShape } from '@prospectus/common/types/allCurrencyShape';
import EnrollButtonLink from '../EnrollButton/EnrollButtonLink';

const messages = defineMessages({
  'prospectus.global.course.enroll-now': {
    id: 'prospectus.global.course.enroll-now',
    description: 'Course "Enroll now" CTA button text',
    defaultMessage: 'Enroll now',
  },
  'prospectus.course.courseModes.header': {
    id: 'prospectus.course.courseModes.header',
    defaultMessage: 'Ways to take this course',
    description: 'Heading for the diagram that will show a user the differences between verified and audit track',
  },
  'prospectus.course.courseModes.subtitle': {
    id: 'prospectus.course.courseModes.subtitle',
    defaultMessage: 'Choose your path when you enroll.',
    description: 'Subtitle for the diagram that will show a user the differences between verified and audit track',
  },
  'prospectus.course.courseModes.verifiedTrack': {
    id: 'prospectus.course.courseModes.verifiedTrack',
    defaultMessage: 'Verified Track',
    description: 'Text for the heading of section showing the offerings of the verified course track',
  },
  'prospectus.course.courseModes.verifiedTrack.secondaryText': {
    id: 'prospectus.course.courseModes.verifiedTrack.secondaryText',
    defaultMessage: 'Earn a certificate',
    description: 'Text for the call to action above the verified track heading',
  },
  'prospectus.course.courseModes.auditTrack': {
    id: 'prospectus.course.courseModes.auditTrack',
    defaultMessage: 'Audit Track',
    description: 'Text for the heading of section showing the offerings of the audit course track',
  },
  'prospectus.course.courseModes.auditTrack.secondaryText': {
    id: 'prospectus.course.courseModes.auditTrack.secondaryText',
    defaultMessage: 'Access course',
    description: 'Text for the call to action above the audit track heading',
  },
  'prospectus.course.courseModes.label.price': {
    id: 'prospectus.course.label.price',
    defaultMessage: 'Price',
    description: 'Text for the label of a section comparing price between tracks',
  },
  'prospectus.course.courseModes.label.access': {
    id: 'prospectus.course.label.access',
    defaultMessage: 'Access to course materials',
    description: 'Text for the label of a section comparing access between tracks',
  },
  'prospectus.course.courseModes.value.free': {
    id: 'prospectus.course.courseModes.value.free',
    defaultMessage: 'Free',
    description: 'The price of an audit course',
  },
  'prospectus.course.courseModes.value.unlimited': {
    id: 'prospectus.course.courseModes.value.unlimited',
    defaultMessage: 'Unlimited',
    description: 'The access for a student who purchases a verified course',
  },
  'prospectus.course.courseModes.value.limited': {
    id: 'prospectus.course.courseModes.value.limited',
    defaultMessage: 'Limited',
    description: 'The access for a student who selects an audit course',
  },
  'prospectus.course.courseModes.value.limited.subtitle': {
    id: 'prospectus.course.courseModes.value.limited.subtitle',
    defaultMessage: 'Expires on {date}',
    description: 'The expiration date for an audit course',
  },
  'prospectus.course.courseModes.label.institutions': {
    id: 'prospectus.course.courseModes.label.institutions',
    defaultMessage: 'World class institutions and universities',
    description: 'Text for the label of a section comparing institutions between tracks',
  },
  'prospectus.course.courseModes.label.edxSupport': {
    id: 'prospectus.course.courseModes.label.edxSupport',
    defaultMessage: 'edX support',
    description: 'Text for the label of a section comparing edX support between tracks',
  },
  'prospectus.course.courseModes.label.certificate': {
    id: 'prospectus.course.courseModes.label.certificate',
    defaultMessage: 'Shareable certificate upon completion',
    description: 'Text for the label of a section comparing certificates between tracks',
  },
  'prospectus.course.courseModes.tooltip.unlimited': {
    id: 'prospectus.course.courseModes.tooltip.unlimited',
    defaultMessage:
      'You will have unlimited access to the course material. After the course ends, you will still have access to the material, but you will no longer be able to submit graded assignments or earn a certificate.',
    descriptions: 'Tooltip text that describes unlimited access for a course',
  },
  'prospectus.course.courseModes.tooltip.limited': {
    id: 'prospectus.course.courseModes.tooltip.limited',
    defaultMessage:
      'In the free audit track you may not be able to access and complete course materials including, but not limited to, graded assignments and exams.',
    descriptions: 'Tooltip text that describes limited access for a course',
  },
  'prospectus.course.courseModes.label.exams': {
    id: 'prospectus.course.courseModes.label.exams',
    defaultMessage: 'Graded assignments and exams',
    description: 'Label for a row that shows whether or not a course has graded assignments and exams',
  },
  'prospectus.course.courseModes.yes': {
    id: 'prospectus.course.courseModes.yes',
    defaultMessage: 'yes',
    description: 'Label for a course feature row where the feature exists on a given track',
  },
  'prospectus.course.courseModes.no': {
    id: 'prospectus.course.courseModes.no',
    defaultMessage: 'no',
    description: 'Label for a course feature row where the feature does not exist on a given track',
  },
  'prospectus.course.courseModes.info-icon': {
    id: 'prospectus.course.courseModes.info-icon',
    defaultMessage: 'More Info',
    description: 'Text label for icon that that triggers a tooltip with more info when you interact with it.',
  },
  'prospectus.course.courseModes.faqLinkIconSrText': {
    id: 'prospectus.course.courseModes.faqLinkIconSrText',
    description: 'Screen reader text for the launch icon on the faq link',
    defaultMessage: 'in a new tab',
  },
  'prospectus.course.courseModes.faqLinkIconTitleText': {
    id: 'prospectus.course.courseModes.faqLinkIconTitleText',
    description: 'Title attribute text for the launch icon on the faq link',
    defaultMessage: 'Opens in a new tab',
  },
});

function CourseModes({
  intl,
  refProp,
  userCurrency,
  coursePrice,
  activeCourseRun,
  uuid,
  showCourseModes,
  showProgramUpsell,
  emailOptIn,
  mobile,
  owners,
  courseType,
  subjects,
  title,
}) {
  const getCourseExpiration = () => {
    if (!activeCourseRun) {
      return null;
    }

    const daysPerWeek = 7;
    const startDate = getCourseStartDate(activeCourseRun);
    const estimatedDuration = activeCourseRun.weeksToComplete;

    if (!startDate || !estimatedDuration) {
      return null;
    }

    const expirationDate = new Date(startDate);
    expirationDate.setDate(expirationDate.getDate() + estimatedDuration * daysPerWeek);

    if (endsWithinThisYear(expirationDate)) {
      return getI18nDayMonthString(expirationDate, intl);
    }

    return getI18nDateString(expirationDate, intl);
  };

  const expirationDate = getCourseExpiration();
  const showAuditModeExpirationDate = useContext(ShowAuditModeExpirationDateContext);

  // logic for returning an empty div is here instead of courseDetail/template level
  // for easier unit testing
  if (!showCourseModes) {
    return <div className={classNames({ 'mt-5': !showProgramUpsell })} />;
  }

  // the aria-label that should appear on the 'none-icon' in the 'ways to take this
  // course' table
  const noneIconLabel = {
    'aria-label': intl.formatMessage(messages['prospectus.course.courseModes.no']),
  };

  return (
    <Container size="sm" className={classNames('course-modes', { 'pt-lg-5 mt-lg-2': !showProgramUpsell })}>
      <div tabIndex="-1" ref={refProp} className="nav-target d-lg-flex">
        <div className="left-panel">
          <div className="left-panel-content">
            {!mobile && (
              <h1 className="mb-3 mb-sm-2 pb-sm-1">
                {intl.formatMessage(messages['prospectus.course.courseModes.header'])}
              </h1>
            )}
            <div className="p mb-3">{intl.formatMessage(messages['prospectus.course.courseModes.subtitle'])}</div>

            <EnrollButtonLink
              emailOptIn={emailOptIn}
              buttonClass="course-modes-enroll-btn"
              buttonVariant="primary"
              hideAsSecondaryButton
              trackingID="course-modes"
              uuid={uuid}
              buttonLabel={intl.formatMessage(messages['prospectus.global.course.enroll-now'])}
              owners={owners}
              cohesionVariant={courseType}
              cohesionCategory={subjects.length > 0 ? subjects[0].name : 'not-available'}
              cohesionName={title}
            >
              <div className="d-flex flex-column">
                <h4 className="text-white m-0">
                  {intl.formatMessage(messages['prospectus.global.course.enroll-now'])}
                </h4>
                <div className="x-small"> {getCourseRunTilePrimaryText(activeCourseRun, intl)}</div>
              </div>
            </EnrollButtonLink>
          </div>
        </div>
        <div className="right-panel">
          <div className="track-comparison-table-wrapper">
            <table className="track-comparison-table m-md-auto">
              <tbody>
                <tr className="tr track-headers-row">
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <th scope="col" className="th" />
                  <th scope="col" className="td">
                    <div className="track-header verified d-flex flex-column align-items-center justify-content-center">
                      <h3 className="heading">
                        {intl.formatMessage(messages['prospectus.course.courseModes.verifiedTrack'])}
                      </h3>
                    </div>
                  </th>
                  <th scope="col" className="td">
                    <div className="track-header d-flex flex-column align-items-center justify-content-center">
                      <h3 className="heading">
                        {intl.formatMessage(messages['prospectus.course.courseModes.auditTrack'])}
                      </h3>
                    </div>
                  </th>
                </tr>
                <tr className="tr">
                  <th scope="row" className="th d-flex align-items-center">
                    <h4 className="comparison-header">
                      {intl.formatMessage(messages['prospectus.course.courseModes.label.price'])}
                    </h4>
                  </th>
                  <td className="td">
                    <p className="comparison-item">{formatPrice(coursePrice, userCurrency, intl)}</p>
                  </td>
                  <td className="td">
                    <p className="comparison-item">
                      {intl.formatMessage(messages['prospectus.course.courseModes.value.free'])}
                    </p>
                  </td>
                </tr>
                <tr className="tr">
                  <th scope="row" className="th d-flex align-items-center">
                    <h4 className="comparison-header">
                      {intl.formatMessage(messages['prospectus.course.courseModes.label.access'])}
                    </h4>
                  </th>
                  <td className="td align-top pt-4">
                    <div className="d-flex align-items-center justify-content-center mt-3 mt-sm-1">
                      <p className="comparison-item mb-0">
                        {intl.formatMessage(messages['prospectus.course.courseModes.value.unlimited'])}
                      </p>
                      <div className="info-icon d-inline-block">
                        <OverlayTrigger
                          placement="top"
                          overlay={(
                            <Tooltip id="tooltip-unlimited">
                              {intl.formatMessage(messages['prospectus.course.courseModes.tooltip.unlimited'])}
                            </Tooltip>
                          )}
                        >
                          <IconButton
                            src={InfoOutline}
                            iconAs={Icon}
                            variant="black"
                            alt={intl.formatMessage(messages['prospectus.course.courseModes.info-icon'])}
                            className="info-button"
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                  </td>
                  <td className="td align-top pt-4">
                    <div className="d-flex align-items-center justify-content-center mt-3 mt-sm-1">
                      <p className="comparison-item mb-0">
                        {intl.formatMessage(messages['prospectus.course.courseModes.value.limited'])}
                      </p>
                      <div className="info-icon d-inline-block">
                        <OverlayTrigger
                          placement="top"
                          overlay={(
                            <Tooltip id="tooltip-limited">
                              {intl.formatMessage(messages['prospectus.course.courseModes.tooltip.limited'])}
                            </Tooltip>
                          )}
                        >
                          <IconButton
                            src={InfoOutline}
                            iconAs={Icon}
                            variant="black"
                            alt={intl.formatMessage(messages['prospectus.course.courseModes.info-icon'])}
                            className="info-button"
                          />
                        </OverlayTrigger>
                      </div>
                    </div>
                    {expirationDate && showAuditModeExpirationDate && (
                      <p className="small text-muted">
                        <FormattedMessage
                          {...messages['prospectus.course.courseModes.value.limited.subtitle']}
                          values={{
                            date: expirationDate,
                          }}
                        />
                      </p>
                    )}
                  </td>
                </tr>
                <tr className="tr">
                  <th scope="row" className="th d-flex align-items-center">
                    <h4 className="comparison-header">
                      {intl.formatMessage(messages['prospectus.course.courseModes.label.institutions'])}
                    </h4>
                  </th>
                  <td className="td check-icon">
                    <Check aria-label={intl.formatMessage(messages['prospectus.course.courseModes.yes'])} />
                  </td>
                  <td className="td check-icon">
                    <Check aria-label={intl.formatMessage(messages['prospectus.course.courseModes.yes'])} />
                  </td>
                </tr>
                <tr className="tr">
                  <th scope="row" className="th d-flex align-items-center">
                    <h4 className="comparison-header">
                      {intl.formatMessage(messages['prospectus.course.courseModes.label.edxSupport'])}
                    </h4>
                  </th>
                  <td className="td check-icon">
                    <Check aria-label={intl.formatMessage(messages['prospectus.course.courseModes.yes'])} />
                  </td>
                  <td className="td check-icon">
                    <Check aria-label={intl.formatMessage(messages['prospectus.course.courseModes.yes'])} />
                  </td>
                </tr>
                <tr className="tr">
                  <th scope="row" className="th d-flex align-items-center">
                    <h4 className="comparison-header">
                      {intl.formatMessage(messages['prospectus.course.courseModes.label.certificate'])}
                    </h4>
                  </th>
                  <td className="td check-icon">
                    <Check aria-label={intl.formatMessage(messages['prospectus.course.courseModes.yes'])} />
                  </td>
                  <td className="td none-icon ">
                    <Icon className="m-auto" src={Remove} svgAttrs={noneIconLabel} />
                  </td>
                </tr>
                <tr className="tr">
                  <th scope="row" className="th d-flex align-items-center">
                    <h4 className="comparison-header">
                      {intl.formatMessage(messages['prospectus.course.courseModes.label.exams'])}
                    </h4>
                  </th>
                  <td className="td check-icon">
                    <Check aria-label={intl.formatMessage(messages['prospectus.course.courseModes.yes'])} />
                  </td>
                  <td className="td none-icon ">
                    <Icon className="m-auto" src={Remove} svgAttrs={noneIconLabel} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="text-muted small mt-4 mt-lg-0">
            <FormattedMessage
              id="prospectus.course.courseModes.faqCTA"
              description="CTA containing link directing users to a page of faqs about course tracks"
              defaultMessage="{link} about frequently asked questions on these tracks."
              values={{
                link: (
                  <ProspectusLink
                    href="https://support.edx.org/hc/en-us/sections/360002747833-About-Audit-and-Verified-Courses"
                    className="inline-link muted-link"
                    analytics={{
                      eventName: 'edx.bi.course-about.ways.faq-link.clicked',
                    }}
                    linkProps={{
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }}
                  >
                    <FormattedMessage
                      id="prospectus.course.courseModes.faqLink"
                      description="Text on Link to faq about course tracks"
                      defaultMessage="Read our FAQs"
                      values={{
                        link: (
                          <FormattedMessage
                            id="prospectus.global.title.faqs.abbreviated"
                            description="FAQs label"
                            defaultMessage="FAQs"
                          />
                        ),
                      }}
                    />
                    <Launch
                      className="ml-1 mt-n1"
                      width="20"
                      height="20"
                      role="img"
                      aria-label=""
                      aria-hidden="true"
                      focusable="false"
                      title={intl.formatMessage(messages['prospectus.course.courseModes.faqLinkIconTitleText'])}
                    />
                    <span className="sr-only">
                      {intl.formatMessage(messages['prospectus.course.courseModes.faqLinkIconSrText'])}
                    </span>
                  </ProspectusLink>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </Container>
  );
}

const mapStateToProps = (store) => ({
  userCurrency: store.userCurrency || { ...defaultUserCurrency },
  activeCourseRun: store.activeCourseRun,
});

CourseModes.propTypes = {
  activeCourseRun: PropTypes.shape({
    weeksToComplete: PropTypes.number.isRequired,
  }).isRequired,
  coursePrice: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  refProp: PropTypes.shape(),
  showCourseModes: PropTypes.bool.isRequired,
  showProgramUpsell: PropTypes.bool,
  userCurrency: currencyInfoShape,
  uuid: PropTypes.string.isRequired,
  emailOptIn: PropTypes.bool,
  mobile: PropTypes.bool,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  courseType: PropTypes.string,
  subjects: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  title: PropTypes.string,
};

CourseModes.defaultProps = {
  userCurrency: {},
  refProp: {},
  emailOptIn: false,
  mobile: false,
  showProgramUpsell: false,
  courseType: null,
  subjects: [],
  title: null,
};

export default connect(mapStateToProps)(injectIntl(CourseModes));
